import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, X, ArrowRight, Sparkles, TrendingUp, History } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getOptimizedImageUrl } from "@/lib/image-optimization";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/data/products";
import { cn } from "@/lib/utils";

interface SearchResult {
  id: string;
  name: string;
  slug: string;
  price: number;
  images: string[] | null;
  category: { name: string } | null;
}

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const debounceTimer = setTimeout(() => {
      searchProducts(query);
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [query]);

  const searchProducts = async (searchQuery: string) => {
    setLoading(true);
    const { data } = await supabase
      .from("products")
      .select(`
        id,
        name,
        slug,
        price,
        images,
        category:categories(name)
      `)
      .ilike("name", `%${searchQuery}%`)
      .limit(6);

    setResults((data as unknown as SearchResult[]) || []);
    setLoading(false);
  };

  const handleSelect = (slug: string) => {
    onOpenChange(false);
    setQuery("");
    navigate(`/product/${slug}`);
  };

  const handleViewAll = () => {
    onOpenChange(false);
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setQuery("");
  };

  const trendingSearches = ["Silk Sarees", "Bridal Lehengas", "Gold Embroidered", "Contemporary Silks"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden border-gold/10 shadow-2xl bg-white/95 backdrop-blur-xl">
        
        {/* Search Header */}
        <div className="relative flex items-center gap-4 px-8 py-6 border-b border-gold/5 bg-ivory/30">
          <Search className="w-6 h-6 text-gold animate-pulse" strokeWidth={1.5} />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search for treasures..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 px-0 text-2xl md:text-3xl font-display placeholder:text-muted-foreground/40 bg-transparent h-auto italic py-2"
          />
          {query && (
            <button 
              onClick={() => setQuery("")} 
              className="p-2 hover:bg-gold/10 rounded-full transition-colors text-gold"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Dynamic Body */}
        <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="p-20 text-center">
               <div className="w-12 h-12 border-2 border-gold/20 border-t-gold rounded-full animate-spin mx-auto mb-4" />
               <p className="text-muted-foreground font-light italic">Gathering selections...</p>
            </div>
          ) : query ? (
               results.length === 0 ? (
                <div className="p-20 text-center text-muted-foreground animate-fade-in">
                  <p className="font-display text-2xl text-charcoal mb-2">No matching pieces found</p>
                  <p className="font-light">Try searching for materials, styles, or collections.</p>
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6 px-4">
                     <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Product Results</span>
                     <button onClick={handleViewAll} className="text-[10px] uppercase tracking-widest font-bold text-gold hover:text-charcoal transition-colors">View All Collection</button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {results.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelect(product.slug)}
                        className="group flex items-center gap-5 p-4 bg-gray-50/50 hover:bg-ivory border border-transparent hover:border-gold/20 transition-all text-left rounded-sm"
                      >
                        <div className="w-20 h-28 bg-white border border-border/40 overflow-hidden flex-shrink-0 shadow-sm relative">
                           <img
                            src={getOptimizedImageUrl(product.images?.[0], { width: 150 })}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display text-lg text-charcoal group-hover:text-gold transition-colors truncate leading-tight">
                             {product.name}
                          </p>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                            {product.category?.name || "Collection"}
                          </p>
                          <p className="text-base font-medium text-charcoal mt-3">{formatPrice(product.price)}</p>
                        </div>
                        <ArrowRight size={16} className="text-gold opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                      </button>
                    ))}
                  </div>
                </div>
              )
          ) : (
            /* Suggestions view when empty */
            <div className="p-10 md:p-16 animate-fade-in-up">
               <div className="grid md:grid-cols-2 gap-16">
                  <div>
                     <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-gold mb-8">
                        <TrendingUp size={16} /> Trending Searches
                     </h3>
                     <div className="flex flex-wrap gap-3">
                        {trendingSearches.map(term => (
                           <button 
                             key={term} 
                             onClick={() => setQuery(term)}
                             className="px-6 py-2.5 bg-gray-50 hover:bg-gold hover:text-white border border-border/50 text-sm font-light transition-all rounded-none"
                           >
                              {term}
                           </button>
                        ))}
                     </div>
                  </div>
                  <div>
                     <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-gold mb-8">
                        <Sparkles size={16} /> Curated Collections
                     </h3>
                     <div className="space-y-4">
                        {["Bridal Heritage", "Monochrome Silks", "Artisan Weaves"].map(col => (
                           <Link 
                             key={col} 
                             to="/shop" 
                             onClick={() => onOpenChange(false)}
                             className="flex items-center justify-between group p-3 border-b border-gold/5 hover:border-gold/30 transition-colors"
                           >
                             <span className="font-display text-xl text-charcoal group-hover:italic transition-all">{col}</span>
                             <ArrowRight size={14} className="text-gold" />
                           </Link>
                        ))}
                     </div>
                  </div>
               </div>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-4 bg-charcoal text-[10px] text-white/40 uppercase tracking-[0.4em] text-center font-bold">
           Leestone Heritage Boutique · Establish 1985
        </div>
      </DialogContent>
    </Dialog>
  );
}
